import type { ReactNode } from 'react'
import { Navigate } from 'react-router'
import { useHasAnyAccess, type PermissionCheck } from './permission-check'

interface RequirePermissionProps {
  children: ReactNode
  /** Where to send a user who lacks the permission. Defaults to the landing. */
  redirectTo?: string
  /** Passes if any of these checks passes (OR). */
  anyOf: PermissionCheck[]
}

/**
 * Route guard: renders `children` if the current user passes any of `anyOf`,
 * otherwise redirects (to the landing by default). Wrap a route element:
 *
 * ```tsx
 * element: (
 *   <RequirePermission anyOf={[{ entityType: PermissionEntityType.Permissions, permission: 'View' }]}>
 *     <PermissionsPage />
 *   </RequirePermission>
 * )
 * ```
 *
 * Note: only permission-gated routes are guarded. Auth + super-admin gating stay
 * in ProtectedRoute / the sidebar respectively (super-admin routes remain
 * reachable by URL — menu-hide only, per the agreed plan).
 */
export function RequirePermission({
  children,
  redirectTo = '/',
  anyOf,
}: RequirePermissionProps) {
  if (!useHasAnyAccess(anyOf)) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}
